'use client';

import type { LinkProps as NextLinkProps } from 'next/link';

import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { getTextFromReactNode } from '@sola/utils/react.ts';
import { classifyLink } from '@sola/utils/url.ts';

import { useRouter } from '../../utils/hooks/useRouter.ts';

export type LinkProps = NextLinkProps &
  React.PropsWithChildren & {
    id?: string;
    className?: string;
    isDisabled?: boolean;
    title?: string;
    download?: boolean;
    type?: 'button' | 'submit' | 'reset';
    target?: string;
    rel?: string;
  };

/**
 * Resolves a Next.js href (string or UrlObject) into a plain URL string
 * suitable for a native `<a>` tag.  Prevents `[object Object]` in the DOM.
 */
const resolveHref = (href: LinkProps['href']): string => {
  if (typeof href === 'string') {
    return href;
  }

  const { pathname, query, hash } = href ?? {};
  let url = pathname ?? '';

  if (query) {
    const params =
      typeof query === 'string'
        ? query
        : new URLSearchParams(query as Record<string, string>).toString();
    if (params) {
      url += `?${params}`;
    }
  }

  if (hash) {
    url += hash.startsWith('#') ? hash : `#${hash}`;
  }

  return url;
};

export const Link: React.FC<LinkProps> = ({ isDisabled, ...props }) => {
  const { RouterComponent } = useRouter();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackClick(
      props.title || getTextFromReactNode(props.children) || 'Unknown',
      resolveHref(props.href),
      props.className,
      props.id,
      'Link',
    );
    if (props.onClick) {
      props.onClick(e);
    }
  };

  // When disabled or no router, render a plain anchor
  if (!RouterComponent || isDisabled) {
    // Disabling this rule because the it doesn't apply when explictly setting role="link"
    // @see https://www.scottohara.me/blog/2021/05/28/disabled-links.html
    // @see https://w3c.github.io/html-aria/#example-communicate-a-disabled-link-with-aria
    /* eslint-disable jsx-a11y-x/anchor-has-content -- content is spread via {...props} which includes children */
    return (
      <a
        {...(props as React.ComponentProps<'a'>)}
        {...(isDisabled && {
          'aria-disabled': true,
          role: 'link',
          href: undefined,
        })}
      />
    );
    /* eslint-enable jsx-a11y-x/anchor-has-content */
  }

  // `classifyLink` owns both decisions: whether the browser must do a real
  // navigation, and which tab the link opens in. See the terminology block in
  // @sola/utils/url.ts.
  //
  // A full page load matters for two different reasons: legacy paths need one
  // so the Cloudflare www-router can intercept and fall back to legacy, and
  // other Sola apps (auth, checkout, write, my) need one because they are
  // separate Next apps that this router cannot route into.
  //
  // TODO: Thread currentSiteDomain through RouterProvider in a follow-up so
  // non-dotcom apps do not apply dotcom legacy rules to relative paths or
  // full-load their own absolute URLs.
  const { navigation, target, url } = classifyLink(resolveHref(props.href));

  if (navigation === 'full-load') {
    const resolvedTarget = props.target || target;
    const opensOtherContext = !['_self', '_parent', '_top'].includes(
      resolvedTarget,
    );
    const explicitRel = props.rel?.trim().split(/\s+/).filter(Boolean) ?? [];
    const rel = opensOtherContext
      ? [...new Set([...explicitRel, 'noopener', 'noreferrer'])].join(' ')
      : props.rel;

    /* eslint-disable jsx-a11y-x/anchor-has-content -- content is spread via {...props} which includes children */
    return (
      <a
        {...(props as React.ComponentProps<'a'>)}
        href={url}
        target={resolvedTarget}
        rel={rel}
        onClick={handleClick}
      />
    );
    /* eslint-enable jsx-a11y-x/anchor-has-content */
  }

  return <RouterComponent {...props} onClick={handleClick} />;
};
